import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-20%" });
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    if (status === 'success' || status === 'error') setStatus('idle');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error('Request failed');
      setStatus('success');
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <section className="py-32 bg-[#050505] relative overflow-hidden" id="contact" ref={ref}>
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#FFD700] rounded-full blur-[150px] opacity-[0.03] pointer-events-none"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row gap-16 justify-between items-center mb-24">
          
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="w-full lg:w-1/2"
          >
            <h2 className="text-[#FFD700] font-space-grotesk tracking-widest text-sm uppercase mb-4 flex items-center gap-4">
              <span className="w-12 h-[1px] bg-[#FFD700]"></span>
              Initiate Contact
            </h2>
            <h3 className="text-5xl md:text-7xl font-space-grotesk font-bold uppercase tracking-tight mb-8">
              Let's secure <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-500">your future.</span>
            </h3>
            <p className="text-gray-400 text-lg md:text-xl font-light mb-12 max-w-md">
              Connect with our experts to discuss comprehensive risk mitigation and technology-driven security solutions.
            </p>
            
            <div className="flex flex-col gap-8">
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 uppercase tracking-widest mb-2">Email Us</span>
                <a href="mailto:info@imvula.net" className="text-2xl text-white hover:text-[#FFD700] transition-colors font-space-grotesk">info@imvula.net</a>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 uppercase tracking-widest mb-2">Call Us</span>
                <a href="tel:+270112027700" className="text-2xl text-white hover:text-[#FFD700] transition-colors font-space-grotesk">+27 (0) 11 202 7700</a>
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="w-full lg:w-5/12"
          >
            <div className="bg-white/[0.02] backdrop-blur-2xl border border-white/[0.05] p-8 md:p-12 rounded-2xl relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
              
              <form data-testid="contact-form" className="relative z-10 flex flex-col gap-6" onSubmit={handleSubmit}>
                <div className="flex flex-col gap-2">
                  <label className="text-xs text-gray-400 uppercase tracking-widest">Full Name</label>
                  <input data-testid="contact-name-input" type="text" name="name" required maxLength={100} value={formData.name} onChange={handleChange} className="w-full bg-transparent border-b border-white/10 px-0 py-3 text-white focus:outline-none focus:border-[#FFD700] transition-colors font-light placeholder:text-gray-700" placeholder="John Doe" />
                </div>
                
                <div className="flex flex-col gap-2">
                  <label className="text-xs text-gray-400 uppercase tracking-widest">Email Address</label>
                  <input data-testid="contact-email-input" type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full bg-transparent border-b border-white/10 px-0 py-3 text-white focus:outline-none focus:border-[#FFD700] transition-colors font-light placeholder:text-gray-700" placeholder="john@company.com" />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-xs text-gray-400 uppercase tracking-widest">Message</label>
                  <textarea data-testid="contact-message-input" rows="4" name="message" required maxLength={2000} value={formData.message} onChange={handleChange} className="w-full bg-transparent border-b border-white/10 px-0 py-3 text-white focus:outline-none focus:border-[#FFD700] transition-colors font-light placeholder:text-gray-700 resize-none" placeholder="How can we help you?"></textarea>
                </div>

                <button data-testid="contact-submit-button" type="submit" disabled={status === 'sending'} className="mt-4 w-full group/btn relative py-5 bg-[#FFD700] overflow-hidden rounded-sm cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
                  <div className="absolute inset-0 bg-white transition-transform duration-500 origin-bottom translate-y-full group-hover/btn:translate-y-0"></div>
                  <span className="relative z-10 font-space-grotesk uppercase tracking-wider text-sm font-bold text-black transition-colors duration-500">
                    {status === 'sending' ? 'Sending...' : 'Send Message'}
                  </span>
                </button>

                {status === 'success' && (
                  <p data-testid="contact-success-message" className="text-[#FFD700] text-sm font-space-grotesk tracking-wide text-center">
                    Message sent. Our team will be in touch shortly.
                  </p>
                )}
                {status === 'error' && (
                  <p data-testid="contact-error-message" className="text-red-400 text-sm font-space-grotesk tracking-wide text-center">
                    Something went wrong. Please email us directly at <a href="mailto:info@imvula.net" className="underline">info@imvula.net</a>.
                  </p>
                )}
              </form>
            </div>
          </motion.div>
          
        </div>

        {/* Footer Area */}
        <div className="pt-12 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex flex-col gap-4">
            <img 
              src="https://customer-assets-39nsmqrw.emergentagent.net/job_imvula-refresh/artifacts/e4av7k42_iqp%20white.webp" 
              alt="iMvula Logo" 
              className="h-12 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
            />
          </div>
          <div className="flex gap-6">
            <a href="#" className="text-xs text-gray-500 hover:text-[#FFD700] uppercase tracking-widest font-space-grotesk transition-colors">Privacy Policy</a>
            <a href="#" className="text-xs text-gray-500 hover:text-[#FFD700] uppercase tracking-widest font-space-grotesk transition-colors">Terms of Service</a>
          </div>
          <p className="text-xs text-gray-600 font-inter">
            &copy; {new Date().getFullYear()} iMvula Group. All rights reserved.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Contact;
